import React from "react";
import styled from "styled-components";

const LegalDocument = styled.div`
  max-width: 100%;
  color: #333;
  
  h1 {
    color: #000;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
    border-bottom: 2px solid #dee2e6;
    padding-bottom: 0.5rem;
  }
  
  h2 {
    color: #000;
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1rem;
    line-height: 1.6;
    color: #333;
  }
  
  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
      line-height: 1.5;
      color: #333;
    }
  }
  
  em {
    color: #666;
    font-style: italic;
  }
  
  strong {
    font-weight: 600;
    color: #000;
  }
  
  address {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 1rem;
    margin: 1rem 0;
    font-style: normal;
    color: #333;
  }
`;

export const TermsOfService = () => (
  <LegalDocument>
    <h1>Terms of Service</h1>
    <p><em>Last updated: June 27, 2025</em></p>

    <p>Welcome to <strong>Veritas</strong> ("we," "us," or "our"). These Terms of Service ("Terms") govern your access and use of our platform, software, and services ("Services"). By using Veritas, you agree to be bound by these Terms.</p>

    <h2>1. Eligibility</h2>
    <p>You must be at least 18 years old and authorized to act on behalf of your organization. You represent that all information you provide is accurate and complete.</p>

    <h2>2. Use of Services</h2>
    <p>You may use our Services solely for lawful purposes and in compliance with these Terms and all applicable laws, including HIPAA, Medicaid, and Medicare regulations.</p>
    <p>You agree not to:</p>
    <ul>
      <li>Misuse or attempt to gain unauthorized access to our systems;</li>
      <li>Use the Services to transmit or store infringing, obscene, or otherwise unlawful content;</li>
      <li>Interfere with the integrity or performance of the Services.</li>
    </ul>

    <h2>3. Privacy & HIPAA Compliance</h2>
    <p>We take data security seriously. Veritas is designed to support HIPAA compliance, but it is your responsibility to ensure that your use of our platform meets applicable regulatory requirements.</p>
    <p>For details, please review our <strong>Privacy Policy</strong>.</p>

    <h2>4. Data Ownership</h2>
    <p>You retain ownership of your data. By using the Services, you grant Veritas a limited license to process, store, and transmit your data as necessary to provide the Services.</p>
    <p>We do not sell, rent, or share protected health information (PHI) except as required by law or with your explicit authorization.</p>

    <h2>5. Service Availability</h2>
    <p>We strive to maintain reliable Services, but we do not guarantee uninterrupted access. We may suspend or terminate Services for maintenance, legal, or security reasons.</p>

    <h2>6. Disclaimers & Limitation of Liability</h2>
    <p>The Services are provided "as is" without warranties of any kind. Veritas is not liable for indirect, incidental, or consequential damages related to your use of the Services.</p>

    <h2>7. Modifications</h2>
    <p>We may update these Terms at any time. Continued use of the Services after changes means you accept the updated Terms.</p>

    <h2>8. Termination</h2>
    <p>You may stop using the Services at any time. We reserve the right to suspend or terminate access if you violate these Terms or applicable law.</p>

    <h2>9. Contact</h2>
    <p>For questions or concerns, contact us at:</p>
    <address>
      <strong>Veritas Support</strong><br />
      support@veritasplatform.com
    </address>
  </LegalDocument>
);

export const PrivacyPolicy = () => (
  <LegalDocument>
    <h1>Privacy Policy</h1>
    <p><em>Effective Date: June 27, 2025</em></p>

    <p>Veritas ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information, including Protected Health Information (PHI), when you use our Services.</p>

    <h2>1. Information We Collect</h2>
    <ul>
      <li>Account details (name, email, organization, etc.)</li>
      <li>Usage data and metadata related to claims processing</li>
      <li>PHI as authorized by you or your organization</li>
    </ul>

    <h2>2. How We Use Information</h2>
    <ul>
      <li>To provide and improve our Services</li>
      <li>To support compliance with Medicaid, Medicare, and HIPAA</li>
      <li>To troubleshoot issues and provide customer support</li>
    </ul>

    <h2>3. Sharing & Disclosure</h2>
    <p>We do not sell or rent your data. We only share data:</p>
    <ul>
      <li>With your explicit consent</li>
      <li>To comply with legal obligations</li>
      <li>With service providers under strict data protection agreements</li>
    </ul>

    <h2>4. Data Security</h2>
    <p>We implement technical and organizational safeguards to protect data, including encryption, access controls, and audit logs.</p>

    <h2>5. HIPAA Compliance</h2>
    <p>Veritas is designed to operate in compliance with HIPAA. We enter into Business Associate Agreements (BAAs) as required.</p>

    <h2>6. Your Rights</h2>
    <p>You may request access, correction, or deletion of your personal data by contacting us.</p>

    <h2>7. Contact</h2>
    <p>Questions? Email us at: support@veritasplatform.com</p>
  </LegalDocument>
);

export const BusinessAssociateAgreement = () => (
  <LegalDocument>
    <h1>HIPAA Business Associate Agreement (BAA)</h1>
    <p><em>This BAA is entered into by and between you ("Covered Entity") and Veritas ("Business Associate").</em></p>

    <h2>1. Purpose</h2>
    <p>This Agreement governs the use and disclosure of Protected Health Information (PHI) received or created by Veritas while providing services to the Covered Entity, as required by HIPAA.</p>

    <h2>2. Permitted Uses</h2>
    <p>Veritas may use and disclose PHI solely to perform functions, activities, or services for the Covered Entity, in accordance with HIPAA and this Agreement.</p>

    <h2>3. Safeguards</h2>
    <p>Veritas will implement appropriate safeguards to prevent unauthorized use or disclosure of PHI, including administrative, technical, and physical protections.</p>

    <h2>4. Reporting</h2>
    <p>Veritas shall report any unauthorized use or disclosure of PHI to the Covered Entity promptly upon discovery.</p>

    <h2>5. Termination</h2>
    <p>This Agreement will terminate when all PHI has been returned or destroyed, or when the main service agreement ends. Sections regarding confidentiality will survive termination.</p>

    <h2>6. Compliance</h2>
    <p>Veritas agrees to comply with applicable HIPAA requirements and assist the Covered Entity in meeting its HIPAA obligations.</p>

    <h2>7. Contact</h2>
    <p>For BAA inquiries, contact: legal@veritasplatform.com</p>
  </LegalDocument>
);
